import React from 'react';

import { DateTime } from 'luxon';

import { hockeyFormatter, statFormatter } from '@nhl/formatter-utilities';

import GlobalColumnFunctions from '../../../../../../utils/globalColumnFunctions';

import baseColumnDefinitions from './baseColumnDefinitions';
import { createAggColumn, createTieColumn, getRegistryCellContent, sumCount } from './columnUtils';

const coachNameColumn = (setVideoId) => ({
    Header: baseColumnDefinitions.CreateHeaderCell('Name'),
    id: 'coachId',
    accessor: (val) => val.lastName + val.firstName + val.coachId,
    minWidth: 220,
    maxWidth: 300,
    headerClassName: 'left',
    className: 'left',
    Cell: (cellData) => {
        const createRegistryContent = (values) => (
            <a href={`/coaches/${values.coachId || values.coach.id}`} rel="noopener" target="_blank">
                {values.coachName}
            </a>
        );
        return getRegistryCellContent(cellData, setVideoId, createRegistryContent, 'activeCoach', 'endSeason');
    },
});

const COACH_NAME_COL = {
    Header: baseColumnDefinitions.CreateHeaderCell('Head Coach'),
    id: 'coach.id',
    accessor: 'coachName',
    headerClassName: 'left',
    className: 'left',
    Cell: ({ original }) => {
        const coachId = original.coachId ? original.coachId : original.coach.id;
        return (
            <a
                href={`/coaches/${coachId}`}
                rel="noopener"
                target="_blank"
                className={original.activeCoach ? 'strong' : ''}
            >
                {original.coachName}
            </a>
        );
    },
};

const FRANCHISE_COL = {
    Header: baseColumnDefinitions.CreateHeaderCell('Franchise', 'Franchise', { textAlign: 'left' }),
    accessor: 'franchiseName',
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
    headerClassName: 'left',
    className: 'left',
};

export const winPercentageColumn = {
    Header: baseColumnDefinitions.CreateHeaderCell('Win %', 'W%'),
    accessor: 'winPctg',
    minWidth: 50,
    aggregate: (vals, rows) => {
        const games = sumCount(rows.map((val) => val.games));
        const wins = sumCount(rows.map((val) => val.wins));
        return wins / games;
    },
    Cell: (cellData) => statFormatter.displayValueAs('winPctg')(cellData.value),
    sortType: 'basic',
};

export const pointPercentageColumn = {
    Header: baseColumnDefinitions.CreateHeaderCell('Point %', 'P%'),
    accessor: 'pointPctg',
    minWidth: 50,
    aggregate: (vals, rows) => {
        const games = sumCount(rows.map((val) => val.games));
        const wins = sumCount(rows.map((val) => val.wins));
        const otLosses = sumCount(rows.map((val) => val.otLosses || val.overtimeLosses || 0));
        const ties = sumCount(rows.map((val) => val.ties || 0));
        return (wins * 2 + ties + otLosses) / (games * 2);
    },
    Cell: (cellData) => statFormatter.displayValueAs('pointPctg')(cellData.value),
    sortType: 'basic',
};

const FIRST_SEASON_COL = {
    Header: baseColumnDefinitions.CreateHeaderCell('First Season', 'First Season'),
    accessor: 'startSeason',
    aggregate: (vals, rows) => {
        const seasons = rows.map((val) => val.startSeason);
        return Math.min(...seasons);
    },
    Cell: (cellData) => hockeyFormatter.singleSeasonFormatter(cellData.value),
};

const LAST_SEASON_COL = {
    Header: baseColumnDefinitions.CreateHeaderCell('Last Season', 'Last Season'),
    accessor: 'endSeason',
    aggregate: (vals, rows) => {
        const seasons = rows.map((val) => val.endSeason);
        return Math.max(...seasons);
    },
    Cell: (cellData) => hockeyFormatter.singleSeasonFormatter(cellData.value),
};

const GAMES_COACHED_COL = {
    Header: baseColumnDefinitions.CreateHeaderCell('Games Coached', 'GC'),
    accessor: 'games',
    getProps: GlobalColumnFunctions.getProps,
};

const SEASONS_COL = {
    Header: baseColumnDefinitions.CreateHeaderCell('Seasons', 'Seasons'),
    accessor: 'seasons',
    getProps: GlobalColumnFunctions.getProps,
};

const OTL_COL = {
    ...baseColumnDefinitions.OVERTIME_LOSSES_COL,
    accessor: 'otLosses',
    getProps: GlobalColumnFunctions.getProps,
};

const MILESTONE_DATE_COL = {
    Header: baseColumnDefinitions.CreateHeaderCell('Date'),
    accessor: 'milestoneDate',
    Cell: ({ original }) => DateTime.fromISO(original.milestoneDate).toFormat('DDD'),
    getProps: GlobalColumnFunctions.getProps,
};

const AGE_COL = {
    accessor: 'ageInDays',
    Header: baseColumnDefinitions.CreateHeaderCell('Age: Years/Days', 'Age'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ original }) => {
        if (original.birthDate === null) return '--';
        const milestoneDate = DateTime.fromISO(original.milestoneDate);
        const birthdate = DateTime.fromISO(original.birthDate);
        const { years, days } = birthdate.until(milestoneDate).toDuration(['years', 'days']).toObject();
        return `${years} years, ${typeof days === 'number' ? Math.round(days) : 0} days`;
    },
};

const CUP_WINS_COL = {
    accessor: 'stanleyCups',
    Header: 'Cup Wins',
    getProps: GlobalColumnFunctions.getProps,
};

const CONSEC_CUPS_COL = {
    accessor: 'consecutiveCupWins',
    Header: 'Consecutive Cup Wins',
    getProps: GlobalColumnFunctions.getProps,
};

const STREAK_DETAILS = {
    accessor: 'streakDetails',
    Header: 'Streak Details',
    getProps: GlobalColumnFunctions.getProps,
};

const CONSEC_PLAYOFF_COL = {
    accessor: 'consecSeasons',
    Header: 'Consec. PS',
    getProps: GlobalColumnFunctions.getProps,
};

const APPEARANCES_COL = {
    accessor: 'stanleyCupFinalAppearances',
    Header: 'Appearances',
    getProps: GlobalColumnFunctions.getProps,
};

const LONGEST_STREAK_COL = {
    accessor: 'longestStreak',
    Header: 'Longest Streak',
    getProps: GlobalColumnFunctions.getProps,
};

const LONGEST_STREAK_DESCRIPTION_COL = {
    accessor: 'longestStreakDescription',
    Header: 'Longest Streak Description',
    getProps: GlobalColumnFunctions.getProps,
};

const SEASONS_WON_COL = {
    accessor: 'seasonsWon',
    Header: 'Seasons Won',
    getProps: GlobalColumnFunctions.getProps,
};

const SERIES_COL = {
    accessor: 'seriesCoached',
    Header: 'Series',
    getProps: GlobalColumnFunctions.getProps,
};

const SERIES_NAME_COL = {
    accessor: 'series',
    Header: 'Series',
    getProps: GlobalColumnFunctions.getProps,
};

const SERIES_GAME_NUMBER_COL = {
    accessor: 'seriesGameNumber',
    Header: 'Series Game #',
    getProps: GlobalColumnFunctions.getProps,
};

const SERIES_WINS_COL = {
    accessor: 'seriesWins',
    Header: 'Series Wins',
    getProps: GlobalColumnFunctions.getProps,
};

const SERIES_LOSSES_COL = {
    accessor: 'seriesLosses',
    Header: 'Series Losses',
    getProps: GlobalColumnFunctions.getProps,
};

const INCOMPLETE_SERIES_COL = {
    accessor: 'incompleteSeries',
    Header: 'Incomplete Series',
    getProps: GlobalColumnFunctions.getProps,
};

const SERIES_WIN_PCTG_COL = {
    accessor: 'seriesWinPctg',
    Header: 'Series W%',
    getProps: GlobalColumnFunctions.getProps,
    Cell: (cellData) => cellData.value.toFixed(3),
};

const BOS_SERIES_COL = {
    accessor: 'bestOfSevenSeriesCoached',
    Header: 'Series',
    getProps: GlobalColumnFunctions.getProps,
};

const BOS_SERIES_WINS_COL = {
    accessor: 'bestOfSevenSeriesWins',
    Header: 'Series Wins',
    getProps: GlobalColumnFunctions.getProps,
};

const BOS_SERIES_LOSSES_COL = {
    accessor: 'bestOfSevenSeriesLosses',
    Header: 'Series Losses',
    getProps: GlobalColumnFunctions.getProps,
};

const BOS_INCOMPLETE_SERIES_COL = {
    accessor: 'bestOfSevenIncompleteSeries',
    Header: 'Incomplete Series',
    getProps: GlobalColumnFunctions.getProps,
};

const BOS_SERIES_WIN_PCTG_COL = {
    accessor: 'bestOfSevenSeriesWinPctg',
    Header: 'Series W%',
    getProps: GlobalColumnFunctions.getProps,
    Cell: (cellData) => cellData.value.toFixed(3),
};

const GAME_7S_COL = {
    accessor: 'gameSevenGamesCoached',
    Header: 'G7 GC',
    getProps: GlobalColumnFunctions.getProps,
};

const GAME_7_WINS_COL = {
    accessor: 'gameSevenWins',
    Header: 'G7 W',
    getProps: GlobalColumnFunctions.getProps,
};

const GAME_7_LOSSES_COL = {
    accessor: 'gameSevenLosses',
    Header: 'G7 L',
    getProps: GlobalColumnFunctions.getProps,
};

const GAME_7_WIN_PCTG_COL = {
    accessor: 'gameSevenWinPctg',
    Header: 'G7 W%',
    getProps: GlobalColumnFunctions.getProps,
    Cell: (row) => (row.original.gameSevenWins / row.original.gameSevenGamesCoached).toFixed(3),
};

const GAME_7_STREAK_COL = {
    accessor: 'streak',
    Header: 'Game 7 Win Streak',
    getProps: GlobalColumnFunctions.getProps,
};

const FIRST_GAME_7 = {
    Header: 'First G7',
    accessor: 'firstGameSeven',
    Cell: ({ original }) => (
        <a href={`https://www.nhl.com/gamecenter/${original.firstGameSevenGameId}`} target="_blank">
            {DateTime.fromISO(original.startDate).toLocaleString()}
            {original.firstGameSevenHomeRoad === 'R' ? ' at ' : ' vs. '}
            {original.firstGameSevenOpponentTeamAbbrev}
        </a>
    ),
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
};

const LAST_GAME_7 = {
    Header: 'Last G7',
    accessor: 'lastGameSeven',
    Cell: ({ original }) => (
        <a href={`https://www.nhl.com/gamecenter/${original.lastGameSevenGameId}`} target="_blank">
            {DateTime.fromISO(original.endDate).toLocaleString()}
            {original.lastGameSevenHomeRoad === 'R' ? ' at ' : ' vs. '}
            {original.lastGameSevenOpponentTeamAbbrev}
        </a>
    ),
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
};

const FIRST_GAME_7_SERIES = {
    accessor: 'firstGameSevenSeries',
    Header: 'First G7 Series',
    getProps: GlobalColumnFunctions.getProps,
};

const LAST_GAME_7_SERIES = {
    accessor: 'lastGameSevenSeries',
    Header: 'Last G7 Series',
    getProps: GlobalColumnFunctions.getProps,
};

const GAME_COL = {
    Header: 'Game',
    accessor: 'milestoneDate',
    Cell: ({ original }) => (
        <a href={`https://www.nhl.com/gamecenter/${original.gameId}`} target="_blank">
            {DateTime.fromISO(original.milestoneDate).toLocaleString()}
            {original.homeRoad === 'R' ? ' at ' : ' vs. '}
            {original.opponentTeamAbbrev}
        </a>
    ),
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
};

const SCORE_COL = {
    Header: 'Score',
    accessor: 'gameId',
    Cell: ({ original }) => (
        <a href={`https://www.nhl.com/gamecenter/${original.gameId}`} target="_blank">
            {original.homeRoad === 'R'
                ? original.teamAbbrev + ': ' + original.goalsFor
                : original.opponentTeamAbbrev + ': ' + original.opponentGoalsFor}
            {' @ '}
            {original.homeRoad === 'R'
                ? original.opponentTeamAbbrev + ': ' + original.opponentGoalsFor
                : original.teamAbbrev + ': ' + original.goalsFor}
        </a>
    ),
};

const CAREER_GAME_COL = {
    Header: 'Career Game',
    accessor: 'games',
    getProps: GlobalColumnFunctions.getProps,
};

const createCoachColumnDefinitions = (regularSeason, setVideoId) => [
    {
        accessor: 'id',
        Header: 'ID',
        aggregate: (vals, rows) => rows[0].coachId,
        Cell: (cellData) => (cellData.row.depth === 1 ? 'a' : '') + cellData.value,
        show: window.location.search.toLowerCase().includes('devmode=true'),
    },
    coachNameColumn(setVideoId),
    createAggColumn('Cups', 'stanleyCups', 'Stanley Cups', { headerColumn: true }),
    createAggColumn('GC', 'games', 'Games Coached'),
    createAggColumn('W', 'wins', 'Wins'),
    createAggColumn('L', 'losses', 'Losses'),
    createTieColumn('T', 'ties', 'Ties'),
    createTieColumn('OTL', 'otLosses', 'Overtime Losses'),
    regularSeason ? pointPercentageColumn : winPercentageColumn,
    FIRST_SEASON_COL,
    LAST_SEASON_COL,
];

const COACH_RANK_AND_NAME = [
    { ...baseColumnDefinitions.INDEX_COL, sortType: (a, b) => (a.index > b.index ? 1 : -1) },
    COACH_NAME_COL,
];
const COACH_CAREER_BASE_COLUMNS = [...COACH_RANK_AND_NAME, baseColumnDefinitions.TEAM_ABBREVS];
const COACH_FRANCHISE_BASE_COLUMNS = [...COACH_RANK_AND_NAME, FRANCHISE_COL];
const COACH_GAMES_AND_SEASONS_COLUMNS = [GAMES_COACHED_COL, SEASONS_COL, FIRST_SEASON_COL, LAST_SEASON_COL];
const COACH_STANDINGS_COLUMNS = [
    GAMES_COACHED_COL,
    baseColumnDefinitions.WINS_COL,
    baseColumnDefinitions.LOSSES_COL,
    baseColumnDefinitions.TIES_COL,
    OTL_COL,
    baseColumnDefinitions.WIN_PCTG_COL,
];
const COACH_PLAYOFF_STANDINGS_COLUMNS = [
    GAMES_COACHED_COL,
    baseColumnDefinitions.WINS_COL,
    baseColumnDefinitions.LOSSES_COL,
    baseColumnDefinitions.TIES_COL,
    OTL_COL,
];
const DATE_AND_AGE_COLUMNS = [MILESTONE_DATE_COL, AGE_COL];
const GAME_DATE_AND_AGE_COLUMNS = [GAME_COL, SCORE_COL, AGE_COL];
const COACH_TEAM_COLUMNS = [...COACH_RANK_AND_NAME, baseColumnDefinitions.TEAM_COL];
const COACH_SERIES_COLUMNS = [
    SERIES_COL,
    SERIES_WINS_COL,
    SERIES_LOSSES_COL,
    INCOMPLETE_SERIES_COL,
    SERIES_WIN_PCTG_COL,
];
const COACH_BOS_SERIES_COLUMNS = [
    BOS_SERIES_COL,
    BOS_SERIES_WINS_COL,
    BOS_SERIES_LOSSES_COL,
    BOS_INCOMPLETE_SERIES_COL,
    BOS_SERIES_WIN_PCTG_COL,
];
const GAME_7_COLUMNS = [GAME_7S_COL, GAME_7_WINS_COL, GAME_7_LOSSES_COL, GAME_7_WIN_PCTG_COL];
const COACH_CAREER_MILESTONE_STANDINGS_COLUMNS = [
    ...COACH_CAREER_BASE_COLUMNS,
    ...COACH_STANDINGS_COLUMNS,
    ...DATE_AND_AGE_COLUMNS,
];
const COACH_CAREER_PLAYOFF_MILESTONE_STANDINGS_COLUMNS = [
    ...COACH_CAREER_BASE_COLUMNS,
    ...COACH_PLAYOFF_STANDINGS_COLUMNS,
    ...DATE_AND_AGE_COLUMNS,
];
const COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS = [
    ...COACH_FRANCHISE_BASE_COLUMNS,
    ...COACH_STANDINGS_COLUMNS,
    ...DATE_AND_AGE_COLUMNS,
];
const COACH_FRANCHISE_PLAYOFF_MILESTONE_STANDINGS_COLUMNS = [
    ...COACH_FRANCHISE_BASE_COLUMNS,
    ...COACH_PLAYOFF_STANDINGS_COLUMNS,
    ...DATE_AND_AGE_COLUMNS,
];

export const coachColumnDefinitions = {
    'coach-most-seasons-career': [...COACH_CAREER_BASE_COLUMNS, ...COACH_GAMES_AND_SEASONS_COLUMNS],
    'coach-most-seasons-single-franchise': [...COACH_FRANCHISE_BASE_COLUMNS, ...COACH_GAMES_AND_SEASONS_COLUMNS],
    'coach-most-games-career': [...COACH_CAREER_BASE_COLUMNS, ...COACH_GAMES_AND_SEASONS_COLUMNS],
    'coach-most-games-single-franchise': [...COACH_FRANCHISE_BASE_COLUMNS, ...COACH_GAMES_AND_SEASONS_COLUMNS],
    'coach-most-games-career-including-playoffs': [...COACH_CAREER_BASE_COLUMNS, ...COACH_GAMES_AND_SEASONS_COLUMNS],
    'coach-most-games-single-franchise-including-playoffs': [
        ...COACH_FRANCHISE_BASE_COLUMNS,
        ...COACH_GAMES_AND_SEASONS_COLUMNS,
    ],
    'coach-most-wins-career': [...COACH_CAREER_BASE_COLUMNS, ...COACH_STANDINGS_COLUMNS],
    'coach-most-wins-single-franchise': [...COACH_FRANCHISE_BASE_COLUMNS, ...COACH_STANDINGS_COLUMNS],
    'coach-most-wins-career-including-playoffs': [...COACH_CAREER_BASE_COLUMNS, ...COACH_STANDINGS_COLUMNS],
    'coach-most-wins-single-franchise-including-playoffs': [
        ...COACH_FRANCHISE_BASE_COLUMNS,
        ...COACH_STANDINGS_COLUMNS,
    ],
    'coach-fewest-games-to-100-wins-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-100-wins-single-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-200-wins-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-200-wins-single-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-300-wins-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-300-wins-single-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-400-wins-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-400-wins-single-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-500-wins-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-500-wins-single-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-600-wins-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-700-wins-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-800-wins-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-900-wins-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-1000-wins-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-100-games-from-start-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-100-games-from-start-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-200-games-from-start-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-200-games-from-start-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-300-games-from-start-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-300-games-from-start-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-400-games-from-start-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-400-games-from-start-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-500-games-from-start-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-500-games-from-start-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-600-games-from-start-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-600-games-from-start-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-700-games-from-start-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-700-games-from-start-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-800-games-from-start-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-800-games-from-start-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-900-games-from-start-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-900-games-from-start-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-1000-games-from-start-career': [...COACH_CAREER_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-wins-1000-games-from-start-franchise': [...COACH_FRANCHISE_MILESTONE_STANDINGS_COLUMNS],
    'coach-youngest-first-career-game': [...COACH_TEAM_COLUMNS, ...GAME_DATE_AND_AGE_COLUMNS],
    'coach-youngest-first-career-win': [...COACH_TEAM_COLUMNS, CAREER_GAME_COL, ...GAME_DATE_AND_AGE_COLUMNS],
    'coach-oldest-first-career-game': [...COACH_TEAM_COLUMNS, ...GAME_DATE_AND_AGE_COLUMNS],
    'coach-oldest-first-career-win': [...COACH_TEAM_COLUMNS, CAREER_GAME_COL, ...GAME_DATE_AND_AGE_COLUMNS],
    'coach-most-years-in-playoffs-career': [
        ...COACH_CAREER_BASE_COLUMNS,
        ...COACH_GAMES_AND_SEASONS_COLUMNS,
        CUP_WINS_COL,
    ],
    'coach-most-years-in-playoffs-single-franchise': [
        ...COACH_FRANCHISE_BASE_COLUMNS,
        ...COACH_GAMES_AND_SEASONS_COLUMNS,
        CUP_WINS_COL,
    ],
    'coach-most-consecutive-years-in-playoffs': [
        ...COACH_CAREER_BASE_COLUMNS,
        FIRST_SEASON_COL,
        LAST_SEASON_COL,
        CONSEC_PLAYOFF_COL,
        CUP_WINS_COL,
    ],
    'coach-most-playoff-games-career': [...COACH_CAREER_BASE_COLUMNS, ...COACH_GAMES_AND_SEASONS_COLUMNS, CUP_WINS_COL],
    'coach-most-playoff-games-single-franchise': [
        ...COACH_FRANCHISE_BASE_COLUMNS,
        ...COACH_GAMES_AND_SEASONS_COLUMNS,
        CUP_WINS_COL,
    ],
    'coach-most-stanley-cup-final-appearances-career': [
        ...COACH_CAREER_BASE_COLUMNS,
        SEASONS_COL,
        APPEARANCES_COL,
        CUP_WINS_COL,
    ],
    'coach-most-stanley-cup-final-appearances-single-franchise': [
        ...COACH_FRANCHISE_BASE_COLUMNS,
        SEASONS_COL,
        APPEARANCES_COL,
        CUP_WINS_COL,
    ],
    'coach-most-consecutive-stanley-cup-final-appearances': [
        ...COACH_CAREER_BASE_COLUMNS,
        FIRST_SEASON_COL,
        LAST_SEASON_COL,
        { ...CONSEC_PLAYOFF_COL, Header: 'Consec. SCF' },
        CUP_WINS_COL,
    ],
    'coach-most-stanley-cups-career': [
        ...COACH_RANK_AND_NAME,
        { ...CUP_WINS_COL, Header: 'Cups' },
        LONGEST_STREAK_COL,
        LONGEST_STREAK_DESCRIPTION_COL,
        SEASONS_WON_COL,
    ],
    'coach-most-stanley-cups-single-franchise': [
        ...COACH_FRANCHISE_BASE_COLUMNS,
        { ...CUP_WINS_COL, Header: 'Cups' },
        LONGEST_STREAK_COL,
        LONGEST_STREAK_DESCRIPTION_COL,
        SEASONS_WON_COL,
    ],
    'coach-most-consecutive-stanley-cups': [
        ...COACH_RANK_AND_NAME,
        { ...CUP_WINS_COL, Header: 'Career Cup Wins' },
        CONSEC_CUPS_COL,
        STREAK_DETAILS,
    ],
    'coach-most-series-wins-career': [...COACH_CAREER_BASE_COLUMNS, ...COACH_SERIES_COLUMNS],
    'coach-most-series-wins-single-franchise': [...COACH_FRANCHISE_BASE_COLUMNS, ...COACH_SERIES_COLUMNS],
    'coach-highest-series-win-pctg-career': [...COACH_CAREER_BASE_COLUMNS, ...COACH_SERIES_COLUMNS],
    'coach-highest-series-win-pctg-single-franchise': [...COACH_FRANCHISE_BASE_COLUMNS, ...COACH_SERIES_COLUMNS],
    'coach-most-best-of-seven-series-wins-career': [...COACH_CAREER_BASE_COLUMNS, ...COACH_BOS_SERIES_COLUMNS],
    'coach-most-best-of-seven-series-wins-single-franchise': [
        ...COACH_FRANCHISE_BASE_COLUMNS,
        ...COACH_BOS_SERIES_COLUMNS,
    ],
    'coach-highest-best-of-seven-series-win-pctg-career': [...COACH_CAREER_BASE_COLUMNS, ...COACH_BOS_SERIES_COLUMNS],
    'coach-highest-best-of-seven-series-win-pctg-single-franchise': [
        ...COACH_FRANCHISE_BASE_COLUMNS,
        ...COACH_BOS_SERIES_COLUMNS,
    ],
    'coach-most-playoff-wins-career': [...COACH_CAREER_BASE_COLUMNS, ...COACH_STANDINGS_COLUMNS, CUP_WINS_COL],
    'coach-most-playoff-wins-single-franchise': [
        ...COACH_FRANCHISE_BASE_COLUMNS,
        ...COACH_STANDINGS_COLUMNS,
        CUP_WINS_COL,
    ],
    'coach-fewest-games-to-50-playoff-wins-career': [...COACH_CAREER_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-50-playoff-wins-single-franchise': [...COACH_FRANCHISE_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-100-playoff-wins-career': [...COACH_CAREER_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-100-playoff-wins-single-franchise': [...COACH_FRANCHISE_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-fewest-games-to-150-playoff-wins-career': [...COACH_CAREER_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-playoff-wins-50-games-career': [...COACH_CAREER_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-playoff-wins-50-games-franchise': [...COACH_FRANCHISE_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-playoff-wins-100-games-career': [...COACH_CAREER_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-playoff-wins-100-games-franchise': [...COACH_FRANCHISE_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-playoff-wins-150-games-career': [...COACH_CAREER_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-most-playoff-wins-200-games-career': [...COACH_CAREER_PLAYOFF_MILESTONE_STANDINGS_COLUMNS],
    'coach-youngest-first-playoff-win': [
        ...COACH_RANK_AND_NAME,
        baseColumnDefinitions.TEAM_COL,
        GAME_COL,
        SCORE_COL,
        SERIES_NAME_COL,
        SERIES_GAME_NUMBER_COL,
        AGE_COL,
    ],
    'coach-youngest-first-series-win': [
        ...COACH_RANK_AND_NAME,
        baseColumnDefinitions.TEAM_COL,
        { ...GAME_COL, Header: 'Clinching Game' },
        SCORE_COL,
        SERIES_NAME_COL,
        SERIES_GAME_NUMBER_COL,
        AGE_COL,
    ],
    'coach-youngest-first-stanley-cup-win': [
        ...COACH_RANK_AND_NAME,
        baseColumnDefinitions.TEAM_COL,
        { ...GAME_COL, Header: 'Cup Clinching Game' },
        SCORE_COL,
        SERIES_NAME_COL,
        { ...SERIES_GAME_NUMBER_COL, Header: 'SCF Game #' },
        AGE_COL,
    ],
    'coach-oldest-first-playoff-win': [
        ...COACH_RANK_AND_NAME,
        baseColumnDefinitions.TEAM_COL,
        GAME_COL,
        SCORE_COL,
        SERIES_NAME_COL,
        SERIES_GAME_NUMBER_COL,
        AGE_COL,
    ],
    'coach-oldest-first-series-win': [
        ...COACH_RANK_AND_NAME,
        baseColumnDefinitions.TEAM_COL,
        { ...GAME_COL, Header: 'Clinching Game' },
        SCORE_COL,
        SERIES_NAME_COL,
        SERIES_GAME_NUMBER_COL,
        AGE_COL,
    ],
    'coach-oldest-first-stanley-cup-win': [
        ...COACH_RANK_AND_NAME,
        baseColumnDefinitions.TEAM_COL,
        { ...GAME_COL, Header: 'Cup Clinching Game' },
        SCORE_COL,
        SERIES_NAME_COL,
        { ...SERIES_GAME_NUMBER_COL, Header: 'SCF Game #' },
        AGE_COL,
    ],
    'coach-most-game-7-career': [...COACH_CAREER_BASE_COLUMNS, ...GAME_7_COLUMNS],
    'coach-most-game-7-single-franchise': [...COACH_FRANCHISE_BASE_COLUMNS, ...GAME_7_COLUMNS],
    'coach-most-game-7-wins-career': [...COACH_CAREER_BASE_COLUMNS, ...GAME_7_COLUMNS],
    'coach-most-game-7-wins-single-franchise': [...COACH_FRANCHISE_BASE_COLUMNS, ...GAME_7_COLUMNS],
    'coach-most-consecutive-game-7-wins-career': [
        ...COACH_CAREER_BASE_COLUMNS,
        GAME_7_STREAK_COL,
        FIRST_GAME_7,
        FIRST_GAME_7_SERIES,
        LAST_GAME_7,
        LAST_GAME_7_SERIES,
    ],
};

export default createCoachColumnDefinitions;
